import type { JSX } from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { AlertRuleDto, AlertTestResultDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { useAlerts } from '@/hooks/queries'
import { useCanManageOrgMembers } from '@/stores/session'
import { DEFAULT_TOAST_MS, UNDO_TOAST_MS, useToastStore } from '@/stores/toasts'
import { ConfirmDestructive } from '@/components/ConfirmDestructive'
import { Button } from '@/ui/primitives/Button'
import { SectionHead } from '@/ui/primitives/Surface'
import { EmptyBlock, ErrorBlock, SkeletonGrid } from '../shared/states'
import { problemText } from '../shared/problem'
import { AlertRuleDialog } from './AlertRuleDialog'
import { AlertRuleTile } from './AlertRuleTile'

/**
 * Экран правил алертов (docs/06 §7.1, FR-O4).
 *
 * Смысл экрана: панель нельзя держать открытой круглосуточно, а бот падает
 * ночью. Правило — это единственный способ узнать об этом не постфактум.
 *
 * Доступ — OWNER/ADMIN. Причина не в «админ главнее»: правило хранит адрес
 * доставки, и разрешив OPERATOR'у его править, мы разрешили бы увести
 * уведомления обо всём флоте в чужой чат. Поэтому здесь не «кнопки
 * задизейблены», а экрана нет вовсе — и пункта меню тоже (см. `SideNav`).
 */

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'

export function AlertsScreen(): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)

  const canManage = useCanManageOrgMembers()
  const alerts = useAlerts({}, canManage)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AlertRuleDto | null>(null)
  const [toDelete, setToDelete] = useState<AlertRuleDto | null>(null)
  /**
   * Итоги проверок этой сессии, по id правила. Держим у себя: ответ
   * `{ delivered, error }` нигде не хранится, а вопрос «дошло ли» остаётся
   * актуальным ровно до следующей правки адреса.
   */
  const [testResults, setTestResults] = useState<Record<string, AlertTestResultDto>>({})

  const test = useMutation({
    mutationFn: (rule: AlertRuleDto) => api.alerts.test(rule.id),
    // Прошлый итог снимаем на старте: «не доставлено» рядом с крутящейся
    // кнопкой читается как результат текущей попытки.
    onMutate: (rule: AlertRuleDto) => {
      setTestResults(previous => {
        const { [rule.id]: _dropped, ...rest } = previous
        return rest
      })
    },
    onSuccess: (result, rule) => {
      setTestResults(previous => ({ ...previous, [rule.id]: result }))
    },
    /**
     * Сюда попадает только сбой самого запроса (403, 429 «слишком часто»,
     * упавшая сеть) — неудачная доставка приходит успешным ответом и
     * показывается в карточке. Рейт-лимит здесь строгий: 10 проверок за
     * 10 минут, и про это надо сказать вслух.
     */
    onError: error =>
      push({ message: problemText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const remove = useMutation({
    mutationFn: (rule: AlertRuleDto) => api.alerts.remove(rule.id),
    onSuccess: (_data, rule) => {
      void queryClient.invalidateQueries({ queryKey: qk.alerts.root })
      push({
        message: `Правило «${rule.name}» удалено`,
        tone: 'warning',
        timeoutMs: UNDO_TOAST_MS
      })
    },
    onError: error =>
      push({ message: problemText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const openCreate = (): void => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (rule: AlertRuleDto): void => {
    setEditing(rule)
    setDialogOpen(true)
  }

  // Экран открывается и по прямой ссылке, поэтому мало спрятать пункт меню:
  // без этой ветки OPERATOR увидел бы бесконечную загрузку (запрос выключен).
  if (!canManage) {
    return (
      <EmptyBlock
        title="Алерты доступны владельцу и администраторам"
        note="Правило хранит адрес, куда уходят уведомления обо всём флоте. Право менять его совпадает с правом управлять участниками организации — попросите владельца."
      />
    )
  }

  const items = alerts.data?.items ?? []

  return (
    <>
      <SectionHead
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus aria-hidden className="size-4" />
            Добавить правило
          </Button>
        }
      />

      {alerts.isPending ? (
        <SkeletonGrid count={2} className={GRID} tileClassName="h-56" />
      ) : alerts.isError ? (
        <ErrorBlock
          error={alerts.error}
          onRetry={() => void alerts.refetch()}
          title="Правила алертов не загрузились"
        />
      ) : items.length === 0 ? (
        <EmptyBlock
          title="Правил алертов пока нет"
          note="Панель не открыта круглосуточно, а бот падает ночью. Правило отправляет сообщение в Telegram или на webhook, когда бот выпал из игры, аккаунт разлогинился или сервер кикнул по знакомой причине."
          action={{ label: 'Создать первое правило', onClick: openCreate }}
        />
      ) : (
        <div className={GRID}>
          {items.map((rule, index) => (
            <div
              key={rule.id}
              className="anim-rise"
              style={{ '--i': Math.min(index, 12) } as never}
            >
              <AlertRuleTile
                rule={rule}
                testing={test.isPending && test.variables?.id === rule.id}
                testResult={testResults[rule.id] ?? null}
                onTest={() => test.mutate(rule)}
                onEdit={() => openEdit(rule)}
                onDelete={() => setToDelete(rule)}
              />
            </div>
          ))}
        </div>
      )}

      <AlertRuleDialog open={dialogOpen} rule={editing} onClose={() => setDialogOpen(false)} />

      <ConfirmDestructive
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete === null) return
          remove.mutate(toDelete)
          setToDelete(null)
        }}
        title={`Удалить правило «${toDelete?.name ?? ''}»?`}
        body="Правило и его адрес доставки будут удалены безвозвратно. Уведомления по нему перестанут приходить сразу же."
        expected={toDelete?.name ?? ''}
        loading={remove.isPending}
      />
    </>
  )
}
